/**
 * MONSTER TOWN — AUDIO INSTALL. HUD/AUDIO/TOUCH CREW (src/audio/**).
 *
 * One `installAudio(ctx)` line in main.ts, nothing else. From here the audio
 * crew is entirely event-driven: it never imports another crew, and no other
 * crew imports it. Everyone reaches sound through
 *   bus.emit('audio:cue', { cue: 'coin' })
 * or a `{ kind: 'sound', cue }` feedback on a placeable interaction.
 *
 * Engine band 80-89 (audio) is respected: the only system this file adds is the
 * music lookahead scheduler at order 82.
 */

import './events'
import { bus, type EventName } from '../core/events'
import { engine } from '../core/engine'
import { registerSaveSection, save } from '../core/save'
import { world } from '../core/scene'
import { app, type GameContext } from '../app/context'
import { audio, type VolumeMode } from './engine'
import { music } from './music'
import { checkAllCues, measureCue } from './offline'
import { cueIds, hasCue } from './cues'
import { installVoicePlayback, playVoice } from './voice'

// ---------------------------------------------------------------------------
// save section  (`audio` — claimed in the contract brief)
// ---------------------------------------------------------------------------

interface AudioSave {
  volume: VolumeMode
  music: boolean
}

/**
 * Which bed plays where. DATA — a new room mood is a line here plus a TrackDef.
 * Phase wins over scene kind because the venue and the house are both interiors
 * but should not sound the same.
 */
const PHASE_TRACK: Record<string, string> = {
  monsterCreator: 'creator',
  venueInterior: 'venue',
  houseInterior: 'interior',
  shop: 'interior',
  slotPick: 'creator',
}

/** Game events that should always make a noise, mapped to cue ids. DATA. */
const AUTO_CUES: readonly (readonly [EventName, string])[] = [
  ['economy:unlocked', 'unlock'],
  ['economy:spent', 'buy'],
  ['build:rejected', 'build_reject'],
  ['build:undo', 'undo_pop'],
  ['scene:transitionEnd', 'scene_in'],
]

let musicEnabled = true

function trackForNow(): string | null {
  if (!musicEnabled) return null
  const byPhase = PHASE_TRACK[app.phase]
  if (byPhase !== undefined) return byPhase
  const id = world.activeSceneId
  if (id === null) return 'town'
  const node = world.get(id)
  if (node === undefined) return 'town'
  return node.def.kind === 'interior' ? 'interior' : 'town'
}

function syncTrack(): void {
  music.setTrack(trackForNow())
}

export function installAudio(_ctx: GameContext): void {
  registerSaveSection<AudioSave>(
    {
      id: 'audio',
      version: 1,
      defaults: () => ({ volume: 'full', music: true }),
      serialize: () => ({ volume: audio.volumeMode, music: musicEnabled }),
      hydrate: (raw, c) => {
        if (!c.isRecord(raw)) return c.defaults
        const v = c.str(raw['volume'], c.defaults.volume)
        const volume: VolumeMode = v === 'quiet' || v === 'off' || v === 'full' ? v : 'full'
        return { volume, music: c.bool(raw['music'], c.defaults.music) }
      },
    },
    (v) => {
      audio.setVolumeMode(v.volume)
      musicEnabled = v.music
      bus.emit('audio:volumeChanged', { mode: v.volume })
      syncTrack()
    },
  )

  // iOS: create + resume the context on the FIRST user gesture anywhere on the
  // page. Never at boot — a context made before a gesture is born suspended.
  audio.armUnlock()
  const bootAt = performance.now()
  const onFirstGesture = (): void => {
    const buses = audio.ensure()
    if (buses === null || audio.context === null) return
    music.attach(audio.context, buses)
    syncTrack()
    bus.emit('audio:unlocked', { ms: performance.now() - bootAt })
    window.removeEventListener('pointerdown', onFirstGesture, true)
    window.removeEventListener('touchstart', onFirstGesture, true)
    window.removeEventListener('keydown', onFirstGesture, true)
  }
  window.addEventListener('pointerdown', onFirstGesture, { capture: true, passive: true })
  window.addEventListener('touchstart', onFirstGesture, { capture: true, passive: true })
  window.addEventListener('keydown', onFirstGesture, { capture: true, passive: true })

  // -- the one public entry point everyone else uses ------------------------
  bus.on('audio:cue', (e) => {
    audio.play(e.cue, e.volume ?? 1)
  })

  /**
   * VOICE. The later pass the brief promised has landed: real clips from the
   * shared kids-arcade pipeline, whisper-verified, in `public/voice/`.
   *
   * `playVoice` still ducks the bed, and it still refuses to invent anything —
   * a key with no recorded clip is silence, never synthesis. See
   * `src/audio/voice.ts`. Nothing anywhere waits on it (LAW #4).
   */
  installVoicePlayback()
  bus.on('audio:voice', (e) => {
    playVoice(e.key)
  })

  bus.on('audio:music', (e) => {
    musicEnabled = e.track !== null
    // An unknown track id is ignored by the bed rather than throwing — a crew
    // asking for a mood we have not written yet keeps the current one.
    music.setTrack(e.track)
    save.request('audio:music')
  })

  for (const [name, cue] of AUTO_CUES) {
    bus.on(name, () => {
      audio.play(cue)
    })
  }

  // Coins: a big haul gets the big chime. Never a nag, always a reward.
  bus.on('economy:earned', (e) => {
    audio.play(e.amount >= 20 ? 'coin_big' : 'coin')
  })

  bus.on('app:modeChanged', (e) => {
    audio.play(e.mode === 'build' ? 'mode_build' : 'mode_play')
  })

  bus.on('app:phaseChanged', syncTrack)
  bus.on('scene:entered', syncTrack)

  // The music bed schedules itself in the audio band, off the game clock, so it
  // stops when the game stops and never runs a rogue timer in a background tab.
  engine.addSystem({
    id: 'audio.music',
    order: 82,
    fixed: () => music.update(),
  })

  // -- test surface (its own global; window.__harness stays architect-owned) --
  const api: AudioTestApi = {
    ready: true,
    cues: () => cueIds(),
    has: (id) => hasCue(id),
    play: (id) => audio.play(id),
    measure: (id) => measureCue(id),
    checkAll: () => checkAllCues(),
    volume: () => audio.volumeMode,
    setVolume: (m) => {
      audio.setVolumeMode(m)
      bus.emit('audio:volumeChanged', { mode: m })
      save.request('audio:volume')
    },
    cycleVolume: () => {
      const m = audio.cycleVolume()
      bus.emit('audio:volumeChanged', { mode: m })
      save.request('audio:volume')
      return m
    },
    track: () => music.trackId,
    unlocked: () => audio.unlocked,
    seed: (n) => {
      audio.seed(n)
      music.seed(n)
    },
  }
  window.__audio = api
}

export interface AudioTestApi {
  readonly ready: boolean
  cues(): string[]
  has(id: string): boolean
  play(id: string): boolean
  measure(id: string): Promise<import('./offline').CueMeasure>
  checkAll(): Promise<import('./offline').AudioCheckReport>
  volume(): VolumeMode
  setVolume(mode: VolumeMode): void
  cycleVolume(): VolumeMode
  track(): string | null
  unlocked(): boolean
  seed(n: number): void
}

declare global {
  interface Window {
    __audio?: AudioTestApi
  }
}
